import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card } from '@/components/ui/card';
import { Award, Sparkles, FileText } from 'lucide-react';
import { getRotatingNotificationMessage } from '@shared/client-types';
import VolumeBadgeImage from '@/components/VolumeBadgeImage';
import { useQuery } from '@tanstack/react-query';
import { resolveYesterdayBadges } from '@/lib/yesterdayBadges';
import { resolveDailyTargetMl, resolveFrequencyGoal } from '@/lib/hydrationGoals';
import { format, subDays } from 'date-fns';

interface Message {
  text: string;
  icon?: string;
  isBadge?: boolean;
  badgeImageSrc?: string;
  badgeName?: string;
  isLink?: boolean;
  linkTo?: string;
  imageUrl?: string;
}

interface MessageCarouselProps {
  syncIndex?: number;
  reminderNumber?: number;
  yesterdayBadge?: string;
  timezone?: string;
}

const MessageCarousel = ({
  syncIndex,
  reminderNumber,
  timezone: timezoneProp,
}: MessageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const todayStr = new Date().toLocaleDateString('en-CA');
  const yesterdayStr = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const { data: userProfile } = useQuery<any>({
    queryKey: ['/api/profile'],
    retry: false,
  });

  const { data: todayTracking } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: todayStr }],
    retry: false,
  });

  const { data: yesterdayStats } = useQuery<any>({
    queryKey: ['/api/stats/daily', { date: yesterdayStr }],
    retry: false,
  });

  const { data: yesterdayTracking } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: yesterdayStr }],
    retry: false,
  });

  const timezone = timezoneProp || userProfile?.timezone || 'Asia/Kolkata';
  const resolvedReminderNumber =
    reminderNumber ?? todayTracking?.reminderCount ?? (syncIndex != null ? syncIndex + 1 : 1);
  const rotationSalt = todayTracking?.successfulIntakes ?? 0;

  const motivationalMessage = getRotatingNotificationMessage(resolvedReminderNumber, {
    timezone,
    rotationSalt,
  });

  const fallbackTarget = resolveDailyTargetMl(userProfile);
  const { volumeBadge, frequencyBadge } = resolveYesterdayBadges({
    totalVolume: yesterdayStats?.totalVolume || 0,
    targetVolume: yesterdayStats?.targetVolume || fallbackTarget,
    missCount: yesterdayStats?.missCount ?? yesterdayTracking?.missCount ?? 0,
    successfulIntakes: yesterdayStats?.successfulReminders ?? yesterdayTracking?.successfulIntakes,
    expectedReminders: resolveFrequencyGoal(userProfile),
    totalReminders: yesterdayStats?.totalReminders ?? yesterdayTracking?.reminderCount,
    volumeBadgeName: yesterdayStats?.volumeBadgeName,
    frequencyBadgeName: yesterdayStats?.frequencyBadgeName,
    volumeBadgePercent: yesterdayStats?.volumeBadgePercent,
    badgesFinalized: yesterdayStats?.badgesFinalized,
    activeHoursStart: userProfile?.activeHoursStart,
    activeHoursEnd: userProfile?.activeHoursEnd,
    reminderInterval: userProfile?.reminderInterval,
  });

  const displayMessages: Message[] = [
    {
      text: motivationalMessage.text,
      icon: motivationalMessage.icon,
    },
    {
      // Prefer volume badge when only one sticker fits in the carousel.
      text: `Yesterday: ${volumeBadge.name} · ${frequencyBadge.name}`,
      isBadge: true,
      badgeImageSrc: volumeBadge.imageSrc,
      badgeName: volumeBadge.name,
    },
    {
      text: "Today's content is ready →",
      isLink: true,
      linkTo: '/content',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % displayMessages.length);
        setIsAnimating(false);
      }, 200);
    }, 3000);

    return () => clearInterval(interval);
  }, [displayMessages.length]);

  const currentMessage = displayMessages[currentIndex];

  return (
    <Card
      className={`p-4 bg-lilac-100 border-lilac-300 overflow-hidden transition-all duration-200 ${
        isAnimating ? 'opacity-0 transform -translate-y-1' : 'opacity-100 transform translate-y-0'
      }`}
    >
      {currentMessage.isLink ? (
        <Link href={currentMessage.linkTo || '/content'}>
          <div className="flex items-center justify-center gap-3 cursor-pointer hover:opacity-80 transition-opacity min-w-0">
            <FileText className="w-5 h-5 text-purple-600 shrink-0" />
            <p className="text-base text-deep-700 font-medium underline min-w-0 break-words text-center">
              {currentMessage.text}
            </p>
          </div>
        </Link>
      ) : currentMessage.isBadge ? (
        <div className="flex items-center justify-center gap-3 min-w-0">
          {currentMessage.badgeImageSrc ? (
            <VolumeBadgeImage
              src={currentMessage.badgeImageSrc}
              alt={currentMessage.badgeName || 'Volume badge'}
              size="sm"
              className="h-6 shrink-0"
            />
          ) : (
            <Award className="w-5 h-5 text-purple-600 shrink-0" />
          )}
          <p className="text-base text-deep-700 font-medium min-w-0 break-words text-center">
            {currentMessage.text}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 min-w-0">
          <Sparkles className="w-5 h-5 text-purple-600 shrink-0" />
          <p className="text-base text-deep-700 text-center font-medium min-w-0 break-words">
            {currentMessage.text}
          </p>
        </div>
      )}

      <div className="flex justify-center gap-1.5 mt-3">
        {displayMessages.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-purple-500 w-4' : 'bg-lilac-400'
            }`}
          />
        ))}
      </div>
    </Card>
  );
};

export default MessageCarousel;
